import { HousingOwnership, ResidenceType, type UpsertResidenceInput } from '../../../../__generated__/globalTypes';
import { isCompleteAddress, type IResidenceAddress } from '../../../../lib/dependants/residenceAddress';

import type { IDependantResidences, IResidence } from '../../../../hooks/queries/useDependantResidences';
import type { IDependant, IDependantPartnership } from '../../../../hooks/queries/useDependants';

// ---------------------------------------------------------------------------
// Residence draft — the in-progress state for the two-page residence section,
// plus every pure helper that hydrates it from the server, validates each
// page's gate, and translates it back into mutation inputs. No React, no
// JSX: this is the testable core the container and the two page components
// build on.
// ---------------------------------------------------------------------------

export interface IFrequentPlace {
  /** `Residence.id` of the existing row to upsert into, or `null` for new entries. */
  id: string | null;
  /** Controlled input text. */
  text: string;
  /** Structured pick — `null` until the user selects a suggestion. */
  address: IResidenceAddress | null;
}

export interface IResidenceFormState {
  /** Existing primary `Residence.id` for this group, or `null` if none yet. Threaded through to `upsertResidence` so a re-submit updates the row. */
  primaryResidenceId: string | null;
  liveWithCaregiver: boolean | null;
  /** Controlled address-input text. */
  addressText: string;
  /** Structured pick — `null` until the user selects a full-address suggestion. */
  address: IResidenceAddress | null;
  residenceType: ResidenceType;
  residenceTypeOther: string;
  homeOwnership: HousingOwnership | null;
  homeOwnershipOther: string;
}

/**
 * Shared "frequently spend time elsewhere" state — kept out of the per-parent
 * form so a partnership living apart still gets a single aggregated list
 * rather than asking the question twice. Persisted as family-shared
 * `Residence` rows (owner null): the question is asked once across the
 * household, so the rows are never pinned to an individual dependant.
 *
 * Removals are NOT tracked here — submit computes a real diff against the
 * hydration snapshot, so anything that started in the DB and isn't in `places`
 * (or with the toggle `false`) gets a `deleteResidence` call automatically.
 * Same applies to flipped `liveWithCaregiver` answers and together↔apart
 * switches.
 */
export interface IFrequentLocationsState {
  toggle: boolean | null;
  /** Free-text addresses, one per row; first row is rendered as soon as the toggle flips on. */
  places: IFrequentPlace[];
}

/**
 * The whole section's in-progress state. `parentBForm` / `liveTogether` are
 * only meaningful for a partnership; a single dependant leaves them at their
 * initial values.
 */
export interface IResidenceDraft {
  /** Partnership "live together?" answer; stays `null` for a single dependant. */
  liveTogether: boolean | null;
  parentAForm: IResidenceFormState;
  parentBForm: IResidenceFormState;
  frequentLocations: IFrequentLocationsState;
}

export const INITIAL_FORM_STATE: IResidenceFormState = {
  primaryResidenceId: null,
  liveWithCaregiver: null,
  addressText: '',
  address: null,
  residenceType: ResidenceType.IndependentLiving,
  residenceTypeOther: '',
  homeOwnership: null,
  homeOwnershipOther: '',
};

export const INITIAL_FREQUENT_LOCATIONS_STATE: IFrequentLocationsState = {
  toggle: null,
  places: [{ id: null, text: '', address: null }],
};

export const INITIAL_RESIDENCE_DRAFT: IResidenceDraft = {
  liveTogether: null,
  parentAForm: INITIAL_FORM_STATE,
  parentBForm: INITIAL_FORM_STATE,
  frequentLocations: INITIAL_FREQUENT_LOCATIONS_STATE,
};

/**
 * A partnership whose members live apart — the only branch that asks for two
 * addresses (one per parent) instead of one shared household answer. A single
 * dependant or a partnership living together both fall through to the shared
 * `parentAForm`.
 */
export const isPartnershipLivingApart = (draft: IResidenceDraft, isPartnership: boolean): boolean =>
  isPartnership && draft.liveTogether === false;

// ---------------------------------------------------------------------------
// Hydration — pre-fill the draft from persisted residences.
// ---------------------------------------------------------------------------

/** Map an existing persisted residence into the form's address shape. */
const residenceToAddress = (r: IResidence): IResidenceAddress => ({
  formatted: r.formatted ?? r.addressLine1 ?? r.city,
  addressLine1: r.addressLine1 ?? null,
  addressLine2: r.addressLine2 ?? null,
  city: r.city,
  state: r.state ?? null,
  postalCode: r.postalCode ?? null,
  country: r.country,
  placeId: r.placeId ?? null,
});

/**
 * Pre-fill a single parent group's form from existing data. The
 * `liveWithCaregiver` answer is read from the persisted
 * `caregiver_relationships.lives_with_caregiver` flag — NOT inferred from
 * residence presence, because a "lives with you = Yes" answer intentionally
 * creates no residence row.
 *
 * The address/living-situation fields are only adopted when the answer is an
 * explicit `false` AND the resolved primary is this dependant's OWN residence.
 * The server resolves a dependant's `primary` through cohabitant/caregiver
 * chains (see ResidenceController), so a primary owned by the caregiver or a
 * partner must never be written back as this dependant's own residence —
 * doing so would reparent another member's row on submit.
 */
export const hydrateFormState = (
  dependantId: string,
  livesWithCaregiver: boolean | null,
  dependantResidence: IDependantResidences | null
): IResidenceFormState => {
  const primary = dependantResidence?.primary ?? null;
  const ownsPrimary = primary !== null && primary.ownerKind === 'dependant' && primary.ownerId === dependantId;
  if (livesWithCaregiver === false && ownsPrimary) {
    return {
      primaryResidenceId: primary.id,
      liveWithCaregiver: false,
      addressText: primary.formatted ?? primary.addressLine1 ?? '',
      address: residenceToAddress(primary),
      residenceType: primary.residenceType,
      residenceTypeOther: primary.residenceTypeOther ?? '',
      homeOwnership: primary.ownership ?? null,
      homeOwnershipOther: primary.ownershipOther ?? '',
    };
  }
  return { ...INITIAL_FORM_STATE, liveWithCaregiver: livesWithCaregiver };
};

/**
 * Pre-fill the shared frequent-locations state by aggregating each dependant's
 * `other` residences into one deduped list. The same `Residence` id can appear
 * under multiple dependants (family-shared rows); the Map collapses those into
 * a single entry.
 */
export const hydrateFrequentLocations = (
  dependantResidences: readonly IDependantResidences[]
): IFrequentLocationsState => {
  const byId = new Map<string, IResidence>();
  for (const { other } of dependantResidences) {
    for (const r of other) {
      if (!byId.has(r.id)) {
        byId.set(r.id, r);
      }
    }
  }
  if (byId.size === 0) {
    return INITIAL_FREQUENT_LOCATIONS_STATE;
  }
  return {
    toggle: true,
    places: [...byId.values()].map((r) => ({
      id: r.id,
      text: r.formatted ?? r.addressLine1 ?? r.city,
      address: residenceToAddress(r),
    })),
  };
};

/**
 * Build the whole draft from persisted server data. Composed from
 * {@link hydrateFormState} (per parent) and {@link hydrateFrequentLocations}
 * (shared). `dependants` must be non-empty — callers gate on the residence
 * query having resolved at least one dependant.
 */
export const buildResidenceDraft = (
  dependants: readonly IDependant[],
  dependantResidences: readonly IDependantResidences[],
  partnership: IDependantPartnership | null
): IResidenceDraft => {
  const residencesById = new Map(dependantResidences.map((d) => [d.dependantId, d]));
  const dependantA = dependants[0];
  const dependantB = dependants[1] ?? null;
  const aResidences = residencesById.get(dependantA.id) ?? null;
  const bResidences = dependantB !== null ? (residencesById.get(dependantB.id) ?? null) : null;

  return {
    liveTogether: dependantB !== null ? (partnership?.liveTogether ?? null) : null,
    parentAForm: hydrateFormState(dependantA.id, dependantA.livesWithCaregiver, aResidences),
    parentBForm:
      dependantB !== null
        ? hydrateFormState(dependantB.id, dependantB.livesWithCaregiver, bResidences)
        : INITIAL_FORM_STATE,
    frequentLocations: hydrateFrequentLocations(dependantResidences),
  };
};

/**
 * Snapshot every `Residence.id` this section is responsible for (primary +
 * frequent), captured at hydration so the submit-time diff can delete the ones
 * the user dropped. Excludes caregiver-owned (`user`) residences: a dependant's
 * resolved `primary` can be the caregiver's home (via `lives_with_caregiver`),
 * and that row must never be deleted by this section.
 */
export const snapshotResidenceIds = (dependantResidences: readonly IDependantResidences[]): ReadonlySet<string> => {
  const ids = new Set<string>();
  for (const { primary, other } of dependantResidences) {
    if (primary !== null && primary.ownerKind !== 'user') {
      ids.add(primary.id);
    }
    for (const r of other) {
      if (r.ownerKind !== 'user') {
        ids.add(r.id);
      }
    }
  }
  return ids;
};

// ---------------------------------------------------------------------------
// Submit builders — translate draft state into mutation inputs.
//
//   - "Live with you = Yes" → no primary residence (the caregiver's existing
//     address covers them too). Frequent places (if any) are still created.
//   - "Live with you = No" → one primary `Residence` per parent group.
//   - "Frequently away = Yes" → one non-primary `Residence` per typed
//     location, family-shared (no owner) since the question is asked once for
//     the whole household.
//
// Together / single: the primary is owned by the first dependant (the schema
// allows at-most-one of userId/dependantId per row, so two dependants can't
// co-own a single residence). Apart: each parent's form drives its own
// primary, owned by their respective dependant.
// ---------------------------------------------------------------------------

const buildPrimaryInputForGroup = (dependantId: string, form: IResidenceFormState): UpsertResidenceInput | null => {
  if (form.liveWithCaregiver !== false || form.address === null) {
    return null;
  }
  const addr = form.address;
  // residenceId set when editing an already-saved primary → the mutation
  // updates that row in place; null → a new Residence is created. Ownership
  // pointers are create-only on the wire, so they're ignored in the update path.
  return {
    residenceId: form.primaryResidenceId,
    dependantId,
    userId: null,
    isPrimary: true,
    residenceType: form.residenceType,
    residenceTypeOther: form.residenceType === ResidenceType.Other ? form.residenceTypeOther.trim() || null : null,
    addressLine1: addr.addressLine1,
    addressLine2: addr.addressLine2,
    city: addr.city,
    state: addr.state,
    postalCode: addr.postalCode,
    country: addr.country,
    placeId: addr.placeId,
    formatted: addr.formatted,
    ownership: form.residenceType === ResidenceType.IndependentLiving ? form.homeOwnership : null,
    ownershipOther: form.homeOwnership === HousingOwnership.Other ? form.homeOwnershipOther.trim() || null : null,
  };
};

/** Primary-residence inputs for every parent group whose answer is "live with you = No". */
export const buildPrimaryResidenceInputs = (
  draft: IResidenceDraft,
  dependants: readonly IDependant[],
  isPartnership: boolean
): UpsertResidenceInput[] => {
  const dependantA = dependants[0];
  const dependantB = dependants[1] ?? null;
  const groups = isPartnershipLivingApart(draft, isPartnership)
    ? [
        { dependantId: dependantA.id, form: draft.parentAForm },
        ...(dependantB !== null ? [{ dependantId: dependantB.id, form: draft.parentBForm }] : []),
      ]
    : [{ dependantId: dependantA.id, form: draft.parentAForm }];

  const inputs: UpsertResidenceInput[] = [];
  for (const { dependantId, form } of groups) {
    const input = buildPrimaryInputForGroup(dependantId, form);
    if (input !== null) {
      inputs.push(input);
    }
  }
  return inputs;
};

/**
 * Translate the shared frequent-locations state into 0..N non-primary
 * `Residence` inputs. Frequent locations are always family-shared (owner null).
 * Returns `[]` when the toggle is anything other than `true`, even if the user
 * typed into a row before flipping back.
 */
export const buildFrequentPlaceInputs = (state: IFrequentLocationsState): UpsertResidenceInput[] => {
  if (state.toggle !== true) {
    return [];
  }
  const inputs: UpsertResidenceInput[] = [];
  for (const place of state.places) {
    const addr = place.address;
    // Defensive: `isFrequentLocationsReady` already blocks Continue on a
    // city-less pick, so an incomplete row here means a code path slipped past
    // the gate — never emit an empty `city` to the wire regardless.
    if (!isCompleteAddress(addr)) {
      continue;
    }
    inputs.push({
      residenceId: place.id,
      dependantId: null,
      userId: null,
      isPrimary: false,
      residenceType: ResidenceType.FrequentLocation,
      residenceTypeOther: null,
      addressLine1: addr.addressLine1,
      addressLine2: addr.addressLine2,
      city: addr.city,
      state: addr.state,
      postalCode: addr.postalCode,
      country: addr.country,
      placeId: addr.placeId,
      formatted: addr.formatted,
      ownership: null,
      ownershipOther: null,
    });
  }
  return inputs;
};

export interface ICaregiverRelationshipUpdate {
  caregiverRelationshipId: string;
  livesWithCaregiver: boolean;
}

/**
 * The "live with you?" answers to persist onto the caregiver-relationship rows
 * the review step already created. A partnership living apart answers per
 * parent; everything else answers once (`parentAForm`) for every dependant's
 * edge. Rows whose answer is still `null` are dropped.
 */
export const buildCaregiverRelationshipUpdates = (
  draft: IResidenceDraft,
  dependants: readonly IDependant[],
  isPartnership: boolean
): ICaregiverRelationshipUpdate[] => {
  const dependantA = dependants[0];
  const dependantB = dependants[1] ?? null;
  const raw: { caregiverRelationshipId: string; livesWithCaregiver: boolean | null }[] = isPartnershipLivingApart(
    draft,
    isPartnership
  )
    ? [
        {
          caregiverRelationshipId: dependantA.caregiverRelationshipId,
          livesWithCaregiver: draft.parentAForm.liveWithCaregiver,
        },
        ...(dependantB !== null
          ? [
              {
                caregiverRelationshipId: dependantB.caregiverRelationshipId,
                livesWithCaregiver: draft.parentBForm.liveWithCaregiver,
              },
            ]
          : []),
      ]
    : dependants.map((d) => ({
        caregiverRelationshipId: d.caregiverRelationshipId,
        livesWithCaregiver: draft.parentAForm.liveWithCaregiver,
      }));
  return raw.filter((u): u is ICaregiverRelationshipUpdate => u.livesWithCaregiver !== null);
};

/**
 * Residence ids hydrated this session but not present in the upsert set
 * (toggle flipped, address cleared, row removed, together↔apart switch, etc.)
 * — each one gets a `deleteResidence` call.
 */
export const computeOrphanResidenceIds = (
  inputs: UpsertResidenceInput[],
  hydratedIds: ReadonlySet<string>
): string[] => {
  const keptIds = new Set<string>();
  for (const input of inputs) {
    if (input.residenceId !== null && input.residenceId !== undefined) {
      keptIds.add(input.residenceId);
    }
  }
  const orphanIds: string[] = [];
  for (const id of hydratedIds) {
    if (!keptIds.has(id)) {
      orphanIds.push(id);
    }
  }
  return orphanIds;
};

// ---------------------------------------------------------------------------
// Validation — page 1 (arrangement) gates on the toggles; page 2 (details)
// gates on the addresses + frequent locations.
// ---------------------------------------------------------------------------

/** Whether a single parent group's primary-residence answer is complete enough to submit. */
export const isFormReady = (state: IResidenceFormState): boolean => {
  if (state.liveWithCaregiver === null) {
    return false;
  }
  if (state.liveWithCaregiver === false) {
    // A primary residence must be a full street address — `addressLine1` is
    // only populated for street-level Places picks (or a hydrated row) — and
    // must carry a city/country like any other residence.
    if (!isCompleteAddress(state.address) || state.address?.addressLine1 == null) {
      return false;
    }
    if (state.residenceType === ResidenceType.Other && state.residenceTypeOther.trim().length === 0) {
      return false;
    }
    if (state.residenceType === ResidenceType.IndependentLiving) {
      if (state.homeOwnership === null) {
        return false;
      }
      if (state.homeOwnership === HousingOwnership.Other && state.homeOwnershipOther.trim().length === 0) {
        return false;
      }
    }
  }
  return true;
};

export const isFrequentLocationsReady = (state: IFrequentLocationsState): boolean => {
  if (state.toggle === null) {
    return false;
  }
  if (state.toggle === true) {
    // At least one real pick, and every pick must resolve to a city/country — a
    // row the user picked but that came back city-less blocks Continue so it
    // can't be silently dropped at submit (see `isCompleteAddress`).
    const picked = state.places.filter((p) => p.address !== null);
    return picked.length > 0 && picked.every((p) => isCompleteAddress(p.address));
  }
  return true;
};

/**
 * Page 1 gate: every living-arrangement toggle is answered. For a partnership
 * the "live together?" answer is required; living apart additionally requires
 * both per-parent "live with you?" answers, otherwise just the shared one.
 * Address fields are NOT checked here — that's page 2's job.
 */
export const isArrangementReady = (draft: IResidenceDraft, isPartnership: boolean): boolean => {
  if (isPartnership && draft.liveTogether === null) {
    return false;
  }
  if (isPartnershipLivingApart(draft, isPartnership)) {
    return draft.parentAForm.liveWithCaregiver !== null && draft.parentBForm.liveWithCaregiver !== null;
  }
  return draft.parentAForm.liveWithCaregiver !== null;
};

/**
 * Page 2 gate: addresses for any non-cohabiting parent are complete and the
 * shared frequent-locations answer is valid. Reuses {@link isFormReady}, which
 * also re-asserts the page 1 toggle is set.
 */
export const isDetailsReady = (draft: IResidenceDraft, isPartnership: boolean): boolean => {
  if (!isFrequentLocationsReady(draft.frequentLocations)) {
    return false;
  }
  if (isPartnershipLivingApart(draft, isPartnership)) {
    return isFormReady(draft.parentAForm) && isFormReady(draft.parentBForm);
  }
  return isFormReady(draft.parentAForm);
};

/**
 * Whether a returning user should open on the details page instead of the
 * arrangement page. A completed section resumes on the back half (addresses +
 * frequent locations) rather than re-asking the living-arrangement toggles.
 *
 * Decided from the living-arrangement answers, which live on the dependant /
 * partnership rows (`useDependants`), NOT on the residence-address query. That
 * query loads lazily — after a refresh + back-nav from a later step it may not
 * have resolved yet — so reading the answers from the always-warm dependants
 * query lets the entry page resolve correctly without waiting on it. Nothing is
 * written until the details Continue, so those answers are only present once the
 * whole section was completed.
 */
export const shouldStartOnDetails = (
  dependants: readonly IDependant[],
  dependantResidences: readonly IDependantResidences[],
  partnership: IDependantPartnership | null,
  isPartnership: boolean
): boolean => {
  if (dependants.length === 0) {
    return false;
  }
  return isArrangementReady(buildResidenceDraft(dependants, dependantResidences, partnership), isPartnership);
};
